import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchBookById } from "../api";
import BookCard from "../components/BookCard";
import Cover from "../components/BookCover";

const BookPreviewFlipbook = lazy(() => import("../components/BookPreviewFlipbook"));

const CATS = [
    { id: "investing", label: "Investing" },
    { id: "finance", label: "Finance" },
    { id: "business", label: "Business" },
    { id: "marketing", label: "Marketing" },
    { id: "career", label: "Career" },
    { id: "hr", label: "HR & People" },
];

export default function BookDetail({ book, cart, addCart, allBooks = [] }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const [resolvedBook, setResolvedBook] = useState(book || null);
    const [loading, setLoading] = useState(false);
    const [showAllDesc, setShowAllDesc] = useState(false);
    const [activeTab, setActiveTab] = useState("inside");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const bookFromList = useMemo(() => {
        if (!id) return null;
        return allBooks.find((item) => item._id === id) || null;
    }, [allBooks, id]);

    useEffect(() => {
        if (id) {
            if (bookFromList) {
                setResolvedBook(bookFromList);
                return;
            }

            let cancelled = false;
            setLoading(true);

            fetchBookById(id)
                .then((nextBook) => {
                    if (!cancelled) setResolvedBook(nextBook);
                })
                .catch(() => {
                    if (!cancelled) setResolvedBook(null);
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });

            return () => {
                cancelled = true;
            };
        }

        setResolvedBook(book || null);
    }, [book, bookFromList, id]);

    useEffect(() => {
        setShowAllDesc(false);
        setActiveTab("inside");
        setIsPreviewOpen(false);
    }, [resolvedBook?._id]);

    const effectiveBook = resolvedBook;

    if (loading && !effectiveBook) {
        return (
            <div style={{ minHeight: "100vh", background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center", color: "var(--text3)", fontFamily: "'Nunito Sans',sans-serif" }}>
                    Loading book...
                </div>
            </div>
        );
    }

    if (!effectiveBook) {
        return (
            <div style={{ minHeight: "100vh", background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 64, marginBottom: 16 }}>Book</div>
                    <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, marginBottom: 12 }}>Book not found</h2>
                    <button className="btn btn-forest" onClick={() => navigate("/store")}>Browse Store</button>
                </div>
            </div>
        );
    }

    const related = allBooks
        .filter((item) =>
            item.cat === effectiveBook.cat &&
            item._id !== effectiveBook._id &&
            item.title &&
            item.price != null
        )
        .slice(0, 4);

    const inCart = cart.some((item) => item._id === effectiveBook._id);
    const disc = (item) => item.orig ? Math.round((1 - item.price / item.orig) * 100) : 0;
    const fmtINR = (amount) => `Rs. ${(amount || 0).toLocaleString("en-IN")}`;
    const hasPreview = Boolean(effectiveBook.pdfUrl);

    const descText = effectiveBook.description || "No description available for this book.";

    // Pre-process description in case it contains raw HTML divs/spans
    let processedText = descText;
    if (processedText.includes("<div") || processedText.includes("<span")) {
        processedText = processedText
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/p>/gi, "\n")
            .replace(/<\/li>/gi, "\n")
            .replace(/<\/div>/gi, "\n")
            .replace(/&nbsp;/g, " ")
            .replace(/<[^>]*>/g, "");
    }

    const formattedText = processedText
        .replace(/\t+/g, " - ") // Convert tab characters to clean separators
        .replace(/(Chapter\s+\d+)/gi, "\n$1")
        .replace(/(Annexure\s+[IVX]+)/gi, "\n$1")
        .replace(/(PART\s+\d+)/gi, "\n$1")
        .replace(/\s*-\s*-\s*/g, " - "); // Prevent duplicate dashes

    const listItems = formattedText
        .split("\n")
        .map((segment) => segment.trim())
        .filter(Boolean)
        .map((item) => {
            // Style part/section headers elegantly
            if (item.toUpperCase().startsWith("PART")) {
                return `<strong style="color: var(--forest); font-size: 15.5px; display: block; margin-top: 12px; margin-bottom: 4px; letter-spacing: 0.02em;">${item}</strong>`;
            }
            return item;
        });
    const visibleItems = showAllDesc ? listItems : listItems.slice(0, 6);
    const discount = disc(effectiveBook);

    return (
        <div className="book-detail-main" style={{ minHeight: "100vh", background: "var(--cream)" }}>
            <style>{`
                @media (max-width: 1024px) {
                    .book-detail-main { padding: 24px !important; }
                    .book-detail-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
                    .book-cover-sidebar { width: 100% !important; display: flex !important; flex-direction: column !important; align-items: center !important; }
                    .book-cover-image { margin: 0 auto !important; }
                    .related-section { padding: 40px 24px !important; }
                }
            `}</style>

            <div style={{ padding: "28px 72px 0" }}>
                <button
                    onClick={() => navigate("/store")}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "none",
                        border: "none",
                        color: "var(--text3)",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 28,
                        fontFamily: "'Nunito Sans',sans-serif",
                    }}
                >
                    {"<-"} Back to Store
                </button>

                <div className="book-detail-grid" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 48 }}>
                    <div className="book-cover-sidebar" style={{ width: 220 }}>
                        <div className="book-cover-image">
                            <Cover book={effectiveBook} w={220} h={308} />
                        </div>

                        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                            {inCart ? (
                                <button className="btn btn-forest" style={{ width: "100%", justifyContent: "center", padding: "14px" }} onClick={() => navigate("/cart")}>
                                    View Cart -
                                </button>
                            ) : (
                                <button className="btn btn-forest" style={{ width: "100%", justifyContent: "center", padding: "14px" }} onClick={() => addCart(effectiveBook)}>
                                    Add to Cart - {fmtINR(effectiveBook.price)}
                                </button>
                            )}

                            <button
                                className="btn btn-ghost"
                                style={{ width: "100%", justifyContent: "center" }}
                                onClick={() => window.open(`https://www.lexona.in/ebooks?search=${encodeURIComponent(effectiveBook.title)}`, "_blank")}
                            >
                                Get ebook
                            </button>
                        </div>

                        <div style={{ marginTop: 18, background: "var(--paper)", borderRadius: 14, border: "1.5px solid var(--border)", overflow: "hidden" }}>
                            {[["Pages", effectiveBook.pages || 0], ["Category", CATS.find((item) => item.id === effectiveBook.cat)?.label || effectiveBook.genre || effectiveBook.cat], ["Author", effectiveBook.author]].map(([key, value]) => (
                                <div key={key} style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
                                    <span style={{ color: "var(--text3)" }}>{key}</span>
                                    <span style={{ fontWeight: 700, color: "var(--ink)" }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ paddingTop: 8 }}>
                        {effectiveBook.badge && (
                            <span
                                style={{
                                    display: "inline-block",
                                    background: effectiveBook.badge === "Bestseller" ? "var(--terra)" : effectiveBook.badge === "New" ? "var(--forest)" : "var(--gold)",
                                    color: "white",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    padding: "3px 12px",
                                    borderRadius: 50,
                                    marginBottom: 12,
                                    letterSpacing: "0.05em",
                                }}
                            >
                                {effectiveBook.badge}
                            </span>
                        )}

                        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 6 }}>
                            {effectiveBook.title}
                        </h1>
                        <p style={{ fontSize: 18, color: "var(--text3)", marginBottom: 14, fontStyle: "italic" }}>{effectiveBook.sub}</p>

                        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 22 }}>
                            <span style={{ fontSize: 13, color: "var(--text3)" }}>
                                by <strong style={{ color: "var(--text)" }}>{effectiveBook.author}</strong>
                            </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
                            <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 34, fontWeight: 800, color: "var(--forest)" }}>
                                {fmtINR(effectiveBook.price)}
                            </span>
                            {effectiveBook.orig > effectiveBook.price && (
                                <span style={{ fontSize: 20, color: "var(--text3)", textDecoration: "line-through", marginLeft: 4 }}>
                                    {fmtINR(effectiveBook.orig)}
                                </span>
                            )}
                            {discount > 0 && (
                                <span style={{ background: "var(--terra)", color: "white", fontSize: 13, fontWeight: 800, padding: "4px 14px", borderRadius: 50 }}>
                                    {discount}% OFF
                                </span>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28, marginBottom: 22 }}>
                            <button
                                type="button"
                                onClick={() => setActiveTab("inside")}
                                style={{
                                    border: "1px solid",
                                    borderColor: activeTab === "inside" ? "var(--forest)" : "var(--border)",
                                    background: activeTab === "inside" ? "var(--forest)" : "var(--paper)",
                                    color: activeTab === "inside" ? "white" : "var(--text)",
                                    borderRadius: 999,
                                    padding: "10px 18px",
                                    fontWeight: 700,
                                    fontSize: 14,
                                    cursor: "pointer",
                                }}
                            >
                                What's Inside
                            </button>

                            {hasPreview && (
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("preview")}
                                    style={{
                                        border: "1px solid",
                                        borderColor: activeTab === "preview" ? "var(--forest)" : "var(--border)",
                                        background: activeTab === "preview" ? "var(--forest)" : "var(--paper)",
                                        color: activeTab === "preview" ? "white" : "var(--text)",
                                        borderRadius: 999,
                                        padding: "10px 18px",
                                        fontWeight: 700,
                                        fontSize: 14,
                                        cursor: "pointer",
                                    }}
                                >
                                    Book Preview
                                </button>
                            )}
                        </div>

                        <div
                            style={{
                                background: "var(--paper)",
                                borderRadius: 24,
                                border: "1.5px solid var(--border)",
                                padding: activeTab === "preview" ? 24 : "26px 28px",
                                boxShadow: "var(--shadow-sm)",
                            }}
                        >
                            {activeTab === "inside" && (
                                <>
                                    <div style={{ borderBottom: "1.5px solid var(--border)", marginBottom: 20 }}>
                                        <h3 style={{ padding: "0 0 10px 0", fontFamily: "'Nunito Sans',sans-serif", fontSize: 16, fontWeight: 800, color: "var(--forest)", margin: 0 }}>
                                            What's Inside
                                        </h3>
                                    </div>

                                    {listItems.length > 0 ? (
                                        <ul style={{ paddingLeft: 20, margin: 0, fontSize: 14.5, color: "var(--text2)", lineHeight: 1.8 }}>
                                            {visibleItems.map((item, index) => {
                                                const isHeader = item.startsWith("<strong");
                                                return (
                                                    <li
                                                        key={index}
                                                        style={{
                                                            marginBottom: isHeader ? 8 : 6,
                                                            listStyleType: isHeader ? "none" : "disc",
                                                            marginLeft: isHeader ? -20 : 0
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: item }}
                                                    />
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <p style={{ fontSize: 14.5, color: "var(--text2)", lineHeight: 1.8, marginBottom: 0 }}>
                                            No description available for this book.
                                        </p>
                                    )}

                                    {listItems.length > 6 && (
                                        <button
                                            onClick={() => setShowAllDesc(!showAllDesc)}
                                            style={{
                                                marginTop: 12,
                                                background: "none",
                                                border: "none",
                                                color: "var(--forest)",
                                                fontWeight: 700,
                                                cursor: "pointer",
                                                padding: 0,
                                                fontSize: 13,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            {showAllDesc ? "Show less" : "Read more"}
                                        </button>
                                    )}
                                </>
                            )}

                            {activeTab === "preview" && hasPreview && (
                                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 20, alignItems: "center" }}>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <Cover book={effectiveBook} w={140} h={196} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 10 }}>
                                            Preview This Book
                                        </h3>
                                        <p style={{ color: "var(--text2)", fontSize: 14.5, lineHeight: 1.8, marginBottom: 18 }}>
                                            Open the interactive preview reader to flip through the sample PDF in full screen without leaving this page.
                                        </p>
                                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                            <button
                                                type="button"
                                                className="btn btn-forest"
                                                onClick={() => setIsPreviewOpen(true)}
                                            >
                                                Open Full Screen Preview
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost"
                                                onClick={() => window.open(effectiveBook.pdfUrl, "_blank", "noopener,noreferrer")}
                                            >
                                                Open PDF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isPreviewOpen && hasPreview && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1200,
                        background: "rgba(24, 18, 10, 0.94)",
                        padding: "20px",
                        overflow: "auto",
                    }}
                >
                    <div style={{ maxWidth: 1440, margin: "0 auto" }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 16,
                                flexWrap: "wrap",
                                marginBottom: 18,
                            }}
                        >
                            <div>
                                <h2 style={{ color: "white", fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 800, margin: 0 }}>
                                    {effectiveBook.title}
                                </h2>
                                <p style={{ margin: "8px 0 0", color: "rgba(255,255,255,0.72)", fontSize: 14 }}>
                                    Book Preview
                                </p>
                            </div>
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => window.open(effectiveBook.pdfUrl, "_blank", "noopener,noreferrer")}
                                >
                                    Open PDF
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-forest"
                                    onClick={() => setIsPreviewOpen(false)}
                                >
                                    Close Preview
                                </button>
                            </div>
                        </div>

                        <Suspense
                            fallback={
                                <div
                                    style={{
                                        minHeight: "calc(100vh - 160px)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "rgba(255,255,255,0.82)",
                                        background: "rgba(255,255,255,0.06)",
                                        borderRadius: 28,
                                        border: "1px solid rgba(255,255,255,0.12)",
                                    }}
                                >
                                    Loading preview...
                                </div>
                            }
                        >
                            <BookPreviewFlipbook pdfUrl={effectiveBook.pdfUrl} fullScreen />
                        </Suspense>
                    </div>
                </div>
            )}

            {related.length > 0 && (
                <div className="related-section" style={{ padding: "40px 72px 60px", background: "var(--cream2)", marginTop: 40, borderTop: "1.5px solid var(--border)" }}>
                    <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 24 }}>
                        More in {CATS.find((item) => item.id === effectiveBook.cat)?.label}
                    </h3>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                        {related.map((relatedBook) => (
                            <BookCard
                                key={relatedBook._id}
                                book={relatedBook}
                                onView={(nextBook) => {
                                    navigate(`/book/${nextBook._id}`);
                                    window.scrollTo(0, 0);
                                }}
                                cart={cart}
                                addCart={addCart}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
